import React, { useEffect } from "react";
import moment from "moment";
import { useDispatch, useSelector } from "react-redux";
import { Tooltip } from "@mui/material";
import SideNavLayout from "src/components/layouts/SideNavLayout/SideNavLayout";
import { columnTypes } from "src/components/UI/atoms/ATMTable/ATMTable";
import { getColumns } from "src/utils/auth/getColumns";
import {
  resetState,
  setIsTableLoading,
  setItems,
  setTotalItems,
} from "src/redux/slices/PanVendorDataSlice";
import { AppDispatch, RootState } from "src/redux/store";
import {
  useExportPanVendorDataXlsxMutation,
  useGetPanFormGeneratedListQuery,
} from "src/services/PanFormReportService";
import { formatDateAndTime } from "src/utils/dateAndTime";
import { showToast } from "src/utils/toaster/showToast";
import PanVendorDataListing from "./PanVendorDataListing";

const paramList = [
  "name",
  "srn",
  "email",
  "mobileNumber",
  "adhaarNumber",
  "panNumber",
  "acknowledgementNumber",
  "distributorCode",
  "generatedByName",
];

/**
 * PAN Vendor Data Report — every application whose PAN form has been generated,
 * with an XLSX export in the vendor's own upload sheet layout. The listing is
 * only there to pick the batch (status / type / generated-date range); the
 * export is what the screen exists for, and the API builds it from each form's
 * stored field values, so it carries the whole form-filling data set.
 */
const PanVendorDataListingWrapper = () => {
  const dispatch = useDispatch<AppDispatch>();
  const panVendorDataState: any = useSelector(
    (state: RootState) => state.panVendorData
  );
  const { items, searchValue, rowsPerPage, page, filterBy, dateFilter, orderByValue } =
    panVendorDataState;

  /*
    Req. Branch Code, sent in a way the CURRENTLY DEPLOYED API understands.

    The code is the leading digits of the acknowledgement number, so it needs a
    "starts with" match. Two ways to ask for one:

    - `filterBy: [{ fieldName: "reqBranchCode" }]`, which pan-form.service's
      `generatedFilters` turns into an anchored regex. Precise, but it only
      exists from commit f0aa8f85 onwards — and an API without it does NOT
      ignore the key: the generic filter loop turns it into
      `{ reqBranchCode: { $in: [...] } }`, a field no document has, so EVERY row
      is filtered out. That is the "no data" this filter showed on dev.

    - `searchValue`, which every build turns into
      `{ $regex: `.*${value}.*` }` over the report's search keys (the
      acknowledgement number among them). Passing the code as `^33470` makes
      that `.*^33470.*` — and `^` only matches at position 0, so the pattern
      matches exactly the strings that START with the code. `.*` backtracks to
      empty for it, which is what makes the anchor reachable.

    The second is used, so the filter works on the deployed build with no API
    change, and keeps working after the redeploy. The server-side filter stays
    in the API as the precise path; switch back to it once every environment is
    past f0aa8f85.

    A code takes over the search box while it is selected — the API takes one
    search term, and two cannot be ANDed into it. The filter card says so.
  */
  const reqBranchCode: string =
    filterBy?.find((filter: any) => filter.fieldName === "reqBranchCode")
      ?.value?.[0] || "";

  /** What the API is actually asked to search for. */
  const effectiveSearchValue = reqBranchCode
    ? `^${reqBranchCode}`
    : searchValue;

  /*
    The branch code is deliberately NOT forwarded as a filter: on a build that
    predates f0aa8f85 it would wipe the result set (see above), and on a newer
    one it would merely repeat what `effectiveSearchValue` already asks for.
  */
  const filterByToSend = (filterBy || []).filter(
    (filter: any) => filter.fieldName !== "reqBranchCode"
  );

  useEffect(() => {
    dispatch(resetState());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { data, isFetching, isLoading } = useGetPanFormGeneratedListQuery({
    limit: rowsPerPage,
    searchValue: effectiveSearchValue,
    params: paramList,
    page,
    filterBy: filterByToSend,
    dateFilter,
    orderBy: "generatedDateToShow",
    orderByValue,
    isPaginationRequired: true,
  });

  React.useEffect(() => {
    if (!isFetching && !isLoading) {
      dispatch(setIsTableLoading(false));
      dispatch(setItems(data?.data || []));
      dispatch(setTotalItems(data?.totalItem || 0));
    } else {
      dispatch(setIsTableLoading(true));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, isFetching, data]);

  const [exportXlsx, { isLoading: isExporting }] =
    useExportPanVendorDataXlsxMutation();

  /**
   * Exports the whole filtered set (not just the page on screen) as the vendor
   * sheet. The server streams a real .xlsx, so all this has to do is save it.
   */
  const handleExportXlsx = async () => {
    try {
      const response: any = await exportXlsx({
        limit: 0,
        // Same search/filter the listing asked for, so the sheet is exactly the
        // set on screen — including the Req. Branch Code.
        searchValue: effectiveSearchValue,
        params: paramList,
        page: 1,
        filterBy: filterByToSend,
        dateFilter,
        orderBy: "generatedDateToShow",
        orderByValue,
        isPaginationRequired: false,
      });

      if (!response?.data) {
        showToast("error", "Failed to export XLSX");
        return;
      }

      const blob = new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        `vendor data ${moment().format("DD.MM.YYYY")}.xlsx`
      );
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      showToast("success", "XLSX exported successfully");
    } catch (error) {
      console.error("Error exporting XLSX:", error);
      showToast("error", "Failed to export XLSX");
    }
  };

  const columns: columnTypes[] = [
    {
      field: "generatedDate",
      headerName: "Form Generated",
      flex: "flex-[1_1_0%]",
      renderCell: (row: any) => (
        <div>
          <div className="text-slate-700 font-medium">
            {formatDateAndTime(row?.generatedDate || row?.createdAt, "DD MMM yyyy")}
          </div>
          <div className="text-[13px] font-medium text-slate-400">
            {formatDateAndTime(row?.generatedDate || row?.createdAt, "hh:mm A")}
          </div>
        </div>
      ),
    },
    {
      field: "srn",
      headerName: "SRN",
      flex: "flex-[1.5_1.5_0%]",
      renderCell: (row: any) => (
        <span className="text-primary-main text-[13px] text-ellipsis overflow-hidden">
          {row?.srn || "-"}
        </span>
      ),
    },
    {
      field: "acknowledgementNumber",
      headerName: "Acknowledgement No.",
      flex: "flex-[1.5_1.5_0%]",
      renderCell: (row: any) => (
        <Tooltip title={row?.acknowledgementNumber || ""}>
          <span className="text-[13px] text-ellipsis overflow-hidden">
            {row?.acknowledgementNumber || "-"}
          </span>
        </Tooltip>
      ),
    },
    {
      field: "name",
      headerName: "Name",
      flex: "flex-[1.5_1.5_0%]",
      renderCell: (row: any) => (
        <span className="text-ellipsis overflow-hidden">{row?.name || "-"}</span>
      ),
    },
    {
      field: "mobileNumber",
      headerName: "Mobile",
      flex: "flex-[1_1_0%]",
      renderCell: (row: any) => <span>{row?.mobileNumber || "-"}</span>,
    },
    {
      field: "applicationType",
      headerName: "Type",
      flex: "flex-[1_1_0%]",
      renderCell: (row: any) => <span>{row?.applicationType || "-"}</span>,
    },
    {
      field: "status",
      headerName: "Status",
      flex: "flex-[1_1_0%]",
      renderCell: (row: any) => <span>{row?.status || "-"}</span>,
    },
    {
      field: "distributorCode",
      headerName: "SJBT Code",
      flex: "flex-[1_1_0%]",
      renderCell: (row: any) => <span>{row?.distributorCode || "N/A"}</span>,
    },
    {
      field: "generatedByName",
      headerName: "Generated By",
      flex: "flex-[1.5_1.5_0%]",
      renderCell: (row: any) => (
        <div className="text-primary-main py-[10px]">
          {row?.generatedByName || "-"}
        </div>
      ),
    },
  ];

  return (
    <SideNavLayout>
      <PanVendorDataListing
        columns={getColumns(columns, "PAN_VENDOR_DATA_REPORT")}
        rows={items.map((item: any) => ({ ...item, key: item._id || item.srn }))}
        onExportXlsx={handleExportXlsx}
        isExporting={isExporting}
      />
    </SideNavLayout>
  );
};

export default PanVendorDataListingWrapper;
